import { UnauthorizedError } from "../errors.js";
import type { AuthOptions, HandlerContext, HandlerUtility } from "../types.js";

/**
 * Authentication utility for verifying user identity.
 *
 * @example
 * ```ts
 * import { compose, withAuth } from "next-route-compose";
 *
 * const verifyToken = async (ctx) => {
 *   const token = ctx.request.headers.get("Authorization")?.replace("Bearer ", "");
 *   if (!token) return null;
 *   return await decodeToken(token);
 * };
 *
 * export const GET = compose(
 *   withAuth({ verify: verifyToken })
 * )(async (ctx) => {
 *   // ctx.user is typed and available
 *   return Response.json({ user: ctx.user });
 * });
 * ```
 */
export function withAuth<TUser = unknown>(options: AuthOptions<TUser>): HandlerUtility {
    const {
        verify,
        onUnauthorized = () => new UnauthorizedError().toResponse(),
    } = options;

    return (handler) => {
        return async (context: HandlerContext) => {
            const user = await verify(context);

            if (user === null || user === undefined) {
                return await onUnauthorized(context);
            }

            // Add user to context
            const enrichedContext: HandlerContext = {
                ...context,
                user,
            };

            return await handler(enrichedContext);
        };
    };
}

/**
 * Optional authentication utility - doesn't fail if user is not found,
 * just sets ctx.user to null.
 *
 * @example
 * ```ts
 * export const GET = compose(
 *   withOptionalAuth({ verify: verifyToken })
 * )(async (ctx) => {
 *   if (ctx.user) {
 *     return Response.json({ message: `Hello, ${ctx.user.name}!` });
 *   }
 *   return Response.json({ message: "Hello, guest!" });
 * });
 * ```
 */
export function withOptionalAuth<TUser = unknown>(
    options: Omit<AuthOptions<TUser>, "onUnauthorized">
): HandlerUtility {
    const { verify } = options;

    return (handler) => {
        return async (context: HandlerContext) => {
            const user = await verify(context);

            const enrichedContext: HandlerContext = {
                ...context,
                user: user ?? null,
            };

            return await handler(enrichedContext);
        };
    };
}
